// libs
import React, { useEffect, memo, useCallback, useState, useRef, ReactNode } from 'react';
import { withRouter } from 'react-router';

// components
import { getImageSearchData } from 'shared/store/imageSearch/services';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { isPostRequestAllowed } from 'shared/store/app/selectors';
import { withDataFetching } from 'shared/helpers/dataFetching';

interface IApiProps {
  children?: ReactNode | ((data: any, loadMore: () => void, loading: boolean) => ReactNode);
  imageKey?: string | number;
}

function ImageSearchApi({ children, imageKey }: IApiProps) {
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const fetchingRef = useRef(false);
  const postRequestAllowed = useSelector(isPostRequestAllowed);
  const dispatch = useDispatch();

  const fetchData = useCallback(async (currentPage = 1, append = false) => {
    if (fetchingRef.current) return;
    
    try {
      fetchingRef.current = true;
      setLoading(true);
      const imageToProcess = localStorage.getItem('imageToProcess');
      if (imageToProcess) {
        const response = await dispatch(
          getImageSearchData({
            base64Image: imageToProcess,
            limit: '36',
            page: currentPage.toString(),
          })
        );
        
        if (response && Array.isArray(response)) {
          if (response.length === 0) {
            setHasMore(false);
          } else {
            if (append && fetchedData) {
              setFetchedData([...fetchedData, ...response]);
            } else {
              setFetchedData(response);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error processing image search:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [dispatch, fetchedData]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && !fetchingRef.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchData(nextPage, true);
    }
  }, [fetchData, hasMore, loading, page]);

  // Initial fetch - only run once when component mounts
  useEffect(() => {
    if (postRequestAllowed) {
      setPage(1);
      setHasMore(true);
      fetchData(1, false);
    }
    // Intentionally not including fetchData in dependencies to prevent re-fetching
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postRequestAllowed, imageKey]);

  return <>{typeof children === 'function' ? children(fetchedData, loadMore, loading) : children}</>;
}

export default withRouter(
  withDataFetching(() => Promise.resolve(), { onUpdate: false })(memo(ImageSearchApi))
);